// Layout.js — 왼쪽 사이드바 + 상단바(검색) + 모바일 하단 탭바
import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { getUserInfo } from '../api/dietApi';
import { isTokenValid } from './RequireLogin';
import './Layout.css';

const ICON = {
  home: <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />,
  calendar: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  food: <><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" /></>,
  sparkle: <><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M19 17v4M17 19h4" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></>,
};

const Icon = ({ name }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICON[name]}
  </svg>
);

// App.js 라우트와 1:1
const NAV = [
  { to: '/', label: '오늘', icon: 'home', tab: true },
  { to: '/DietCalendar', label: '캘린더', icon: 'calendar', tab: true },
  { to: '/food-search', label: '음식검색', icon: 'search', tab: true },
  { to: '/food-my', label: '내 음식', icon: 'food' },
  { to: '/DietRecommend', label: '식단추천', icon: 'sparkle', tab: true },
];

const TITLES = [
  ['/DietCalendar', '캘린더'],
  ['/diet/', '식단 기록'],
  ['/food-search', '음식검색'],
  ['/food-my', '내 음식'],
  ['/DietRecommend', '식단추천'],
  ['/mypage', '마이페이지'],
  ['/login', '로그인'],
  ['/signup', '회원가입'],
];

const WEEK = ['일', '월', '화', '수', '목', '금', '토'];

const readAuth = () => {
  const token = localStorage.getItem('token');
  if (token && isTokenValid(token)) return true;
  if (token) {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
  }
  return false;
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [authed, setAuthed] = useState(readAuth);
  const [userName, setUserName] = useState('');

  // 다른 탭 로그인/로그아웃(storage) + 같은 탭 화면 이동 시마다 다시 확인
  useEffect(() => {
    const sync = () => setAuthed(readAuth());
    sync();
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [pathname]);

  useEffect(() => {
    if (!authed) { setUserName(''); return; }
    getUserInfo()
      .then((res) => setUserName(res.data?.userName || localStorage.getItem('userId') || ''))
      .catch(() => setUserName(localStorage.getItem('userId') || ''));
  }, [authed]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    setAuthed(false);
    navigate('/');
  };

  const onSearch = (e) => {
    e.preventDefault();
    const q = e.target.foodNm.value.trim();
    navigate(q ? `/food-search?foodNm=${encodeURIComponent(q)}` : '/food-search');
  };

  const today = new Date();
  const title = pathname === '/'
    ? `${today.getMonth() + 1}월 ${today.getDate()}일 (${WEEK[today.getDay()]})`
    : (TITLES.find(([p]) => pathname.startsWith(p))?.[1] ?? '');

  return (
    <div className="qs-shell">
      <aside className="qs-sidebar">
        <Link className="qs-logo" to="/"><span className="qs-logo-mark">🥗</span>quadSquad</Link>

        <nav className="qs-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `qs-nav-item${isActive ? ' active' : ''}`}>
              <Icon name={n.icon} />{n.label}
            </NavLink>
          ))}
        </nav>

        <div className="qs-sidebar-foot">
          {authed ? (
            <>
              <NavLink to="/mypage" className={({ isActive }) => `qs-nav-item${isActive ? ' active' : ''}`}>
                <Icon name="user" />마이페이지
              </NavLink>
              <div className="qs-user">
                <span className="qs-avatar">{(userName || '?').slice(-2)}</span>
                <span className="qs-user-name">{userName}</span>
                <button className="qs-text-btn" type="button" onClick={logout}>로그아웃</button>
              </div>
            </>
          ) : (
            <div className="qs-auth-btns">
              <button className="btn primary" type="button" onClick={() => navigate('/login')}>로그인</button>
              <button className="btn" type="button" onClick={() => navigate('/signup')}>회원가입</button>
            </div>
          )}
        </div>
      </aside>

      <div className="qs-main">
        <header className="qs-topbar">
          <h1 className="qs-page-title">{title}</h1>
          <form className="qs-search" onSubmit={onSearch}>
            <Icon name="search" />
            <input type="text" name="foodNm" placeholder="음식 검색" autoComplete="off" />
          </form>
        </header>
        <main className="qs-content">{children}</main>
      </div>

      <nav className="qs-tabbar">
        {[...NAV.filter((n) => n.tab), { to: authed ? '/mypage' : '/login', label: authed ? '마이' : '로그인', icon: 'user' }].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `qs-tab${isActive ? ' active' : ''}`}>
            <Icon name={n.icon} />{n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default Layout;
